import { watch } from 'vue';
import { canShowAds, isAdRoute } from '@/config/ads';

export function adContext(auth, route) {
  return {
    routeName: route.name,
    user: auth.currentUser,
    isAuthenticated: auth.isAuthenticated,
    initializing: auth.initializing,
    sessionVerified: auth.sessionVerified,
  };
}

// Removing a script tag cannot undo Google's already-executed Auto ads code.
// Cross into protected pages using a fresh document only after requesting ads.
export function installAdBoundary(router, auth, service, navigate = (url, replace) => {
  window.location[replace ? 'replace' : 'assign'](url);
}) {
  service.isEligible = () => canShowAds(adContext(auth, router.currentRoute.value));
  let leaving = false;
  let timer;
  const leave = (to, replace = false) => {
    if (leaving || !service.requested || canShowAds(adContext(auth, to))) return false;
    leaving = true;
    clearTimeout(timer);
    navigate(router.resolve(to).href, replace);
    return true;
  };
  const removeGuard = router.beforeResolve(to => leave(to) ? false : undefined);
  const checkAccount = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (!service.navigationPending) leave(router.currentRoute.value, true);
    }, 0);
  };
  const navigationFinished = () => { service.navigationPending = false; checkAccount(); };
  const removeAfter = router.afterEach(navigationFinished);
  const removeError = router.onError(navigationFinished);
  const stop = watch(() => [auth.currentUser?.subscription, auth.isAuthenticated,
    auth.currentUser?.freeze, auth.currentUser?.banned], () => {
    // Let an in-flight navigation choose its destination; otherwise reload here
    // when a user upgrades or signs out without navigating.
    checkAccount();
  }, { flush: 'post' });
  const refreshDocument = () => {
    if (leaving || !service.requested) return;
    leaving = true;
    navigate(router.resolve(router.currentRoute.value).href, true);
  };
  const onPageShow = event => { if (event.persisted) refreshDocument(); };
  const onStorage = event => { if (event.key === 'indiefy-user') refreshDocument(); };
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('storage', onStorage);
  return () => {
    clearTimeout(timer); stop(); removeGuard(); removeAfter(); removeError();
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('storage', onStorage);
  };
}

// Official AdSense opt-out for links into signup, checkout, editors and tools.
export function protectAdNavigation(root, router) {
  const protect = () => {
    root.querySelectorAll('a[href]').forEach(link => {
      let url;
      try { url = new URL(link.getAttribute('href'), window.location.href); } catch { return; }
      if (url.origin !== window.location.origin) return;
      const destination = router.resolve(url.pathname);
      if (!isAdRoute(destination.name)) link.setAttribute('data-google-vignette', 'false');
    });
  };
  protect();
  const observer = new MutationObserver(protect);
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  return () => observer.disconnect();
}
